import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import {
  useAddMcpServer,
  useRemoveMcpServer,
} from "@/lib/hooks/useMcpServerMutations";
import { useMcpServers } from "@/lib/hooks/useMcpServers";
import { usePanelStore } from "@/state/stores";
import {
  Loader2,
  MonitorX,
  MoreHorizontal,
  PanelBottomCloseIcon,
  Plus,
} from "lucide-react";
import { useState } from "react";
import type { SerializableMcpConnection } from "../../../../../schemas/codegen";

// Represents a single server item in the list
interface MCPServerListItemProps {
  server: SerializableMcpConnection;
}

function MCPServerListItem({ server }: MCPServerListItemProps) {
  const { connectionState, url, error, authUrl, name, capabilities, id } =
    server;
  const activeChatId = useActiveChatId();
  const removeMcpServer = useRemoveMcpServer();
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    if (!activeChatId) {
      return;
    }

    setIsDisconnecting(true);
    try {
      await removeMcpServer.mutateAsync({
        chatId: activeChatId,
        serverId: id,
      });
    } catch (error) {
      console.error("Failed to disconnect MCP server:", error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const statusColors = {
    ready: "bg-green-500",
    failed: "bg-red-500",
    connecting: "bg-blue-500",
    authenticating: "bg-yellow-500",
    discovering: "bg-purple-500",
  } as const;

  const statusColor =
    statusColors[connectionState as keyof typeof statusColors] || "bg-gray-500";

  return (
    <div className="flex items-center gap-3 rounded-lg bg-neutral-800/80 p-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`h-2 w-2 shrink-0 rounded-full ${statusColor}`} />
        </TooltipTrigger>
        <TooltipContent
          className={
            connectionState === "failed"
              ? "flex items-center gap-2 border-red-900 bg-red-950 text-red-400"
              : ""
          }
        >
          {connectionState === "failed" && error ? (
            <>
              <MonitorX className="h-4 w-4 shrink-0" />
              <p>{error}</p>
            </>
          ) : (
            <p>
              {connectionState.charAt(0).toUpperCase() +
                connectionState.slice(1)}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm text-neutral-300">{name}</div>
        <div className="truncate text-xs text-neutral-500">{url}</div>
        {capabilities && (
          <div className="flex gap-2 mt-1">
            {capabilities.tools && capabilities.tools.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-neutral-600">
                    {capabilities.tools.length} tools
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold text-xs mb-1">
                      Available Tools:
                    </p>
                    <p className="text-xs">{capabilities.tools.join(", ")}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            {capabilities.resources && capabilities.resources.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-neutral-600">
                    {capabilities.resources.length} resources
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-semibold text-xs mb-1">
                      Available Resources:
                    </p>
                    <p className="text-xs">
                      {capabilities.resources.join(", ")}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {connectionState === "authenticating" && authUrl && (
        <Button
          asChild
          variant="outline"
          className="h-auto shrink-0 px-3 py-1 text-xs border-neutral-700 hover:bg-neutral-800/80"
        >
          <a href={authUrl} target="_blank" rel="noopener noreferrer">
            Authenticate
          </a>
        </Button>
      )}
      <FpDropdownMenu>
        <FpDropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 hover:bg-neutral-700/80"
          >
            <MoreHorizontal className="h-4 w-4 text-neutral-400" />
            <span className="sr-only">MCP Server Options</span>
          </Button>
        </FpDropdownMenuTrigger>
        <FpDropdownMenuContent align="end">
          <FpDropdownMenuItem
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            className="text-red-500 hover:!text-red-500 hover:!bg-red-500/10"
          >
            {isDisconnecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              "Disconnect"
            )}
          </FpDropdownMenuItem>
        </FpDropdownMenuContent>
      </FpDropdownMenu>
    </div>
  );
}

// Renders the list of available MCP servers
interface MCPServerListProps {
  connections: Record<string, SerializableMcpConnection>;
}

function MCPServerList({ connections }: MCPServerListProps) {
  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-2 px-4 py-3">
        {Object.values(connections).map((server) => (
          <MCPServerListItem key={server.id} server={server} />
        ))}
      </div>
    </TooltipProvider>
  );
}

// Renders the input and button for adding a new server
function AddMCPServerInput() {
  const activeChatId = useActiveChatId();
  const addMcpServer = useAddMcpServer();
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isValidUrl = (str: string) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!activeChatId) {
      setError("No active chat");
      return;
    }

    if (!url.trim()) {
      setError("URL is required");
      return;
    }

    if (!isValidUrl(url.trim())) {
      setError("Please enter a valid URL");
      return;
    }

    const serverName = name.trim() || new URL(url.trim()).hostname;

    setError(null);
    try {
      await addMcpServer.mutateAsync({
        chatId: activeChatId,
        url: url.trim(),
        name: serverName,
      });
      // Clear form on success
      setUrl("");
      setName("");
    } catch (error) {
      setError(error instanceof Error ? error.message : "Failed to add server");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className="py-3 space-y-2 px-4">
      {error && (
        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          type="url"
          name="url"
          aria-label="Server URL"
          placeholder="Server URL (e.g., https://mcp.example.com/mcp)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          className="bg-neutral-800 basis-1/2"
          disabled={addMcpServer.isPending}
        />
        <Input
          type="text"
          name="name"
          aria-label="Server Name"
          placeholder="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="bg-neutral-800 text-sm flex-1"
          disabled={addMcpServer.isPending}
        />
        <Button
          variant="ghost"
          type="button"
          onClick={handleSubmit}
          disabled={addMcpServer.isPending || !url.trim()}
          className="hover:bg-neutral-700/80"
        >
          {addMcpServer.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="h-4 w-4" />
              Add
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export function McpServersTab() {
  const { closePanel } = usePanelStore();
  const activeChatId = useActiveChatId();
  const { data: connections = {}, isLoading } = useMcpServers(activeChatId);

  return (
    <>
      {/* Title Bar */}
      <header className="flex items-center justify-between bg-neutral-800 py-2 px-4 rounded-t-xl">
        <h3 className="text-sm flex items-center gap-1.5">MCP Servers</h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={closePanel}
          className="h-6 w-6 hover:bg-neutral-700/80"
        >
          <PanelBottomCloseIcon className="w-4 h-4" />
        </Button>
      </header>

      {/* Content Section */}
      <main>
        {isLoading ? (
          <div className="px-4 py-3 space-y-2">
            <Skeleton className="h-16 w-full bg-neutral-800" />
            <Skeleton className="h-16 w-full bg-neutral-800" />
            <Skeleton className="h-16 w-full bg-neutral-800" />
          </div>
        ) : Object.keys(connections as object).length === 0 ? (
          <div className="px-4 py-8 text-center text-neutral-400">
            <p className="text-sm">No MCP servers connected</p>
          </div>
        ) : (
          <MCPServerList
            connections={
              connections as Record<string, SerializableMcpConnection>
            }
          />
        )}
        <AddMCPServerInput />
      </main>
    </>
  );
}
